import React, { useCallback, useEffect, useMemo, memo, useRef, useState } from 'react';
import { isGoogleMapsLocationUrl, normalizeHttpsUrl } from '../../lib/googleMapsUrl.js';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import {
  partnerHasMarketplaceSellerRoles,
  partnerHasActiveStoreRole,
  partnerHasActivePickupRole,
  fetchSellerListingsForManage,
  updateSellerListingStatus,
  listingThumbUrlFromRow,
} from '../../lib/listingsService.js';
import SectionCustodiaPartner from './SectionCustodiaPartner.js';
import SectionPickupPartner from './SectionPickupPartner.js';
import SectionNoticiasPartner from './SectionNoticiasPartner.js';
import {
  fetchReceivedReviews,
  updateSellerPublicReply,
} from '../../lib/reviewsService.js';
import { moderateText } from '../../lib/moderationService.js';
import PartnerRoleBadges from '../components/PartnerRoleBadges.js';
import SurcosRating from '../components/SurcosRating.js';
import Icon from '../components/Icon.js';
import WebTopBar from '../components/WebTopBar.js';
import { DCard, BtnGold, BtnGhost, Avatar } from '../components/MarketplaceAtoms.js';
import {
  PARTNER_ROLE_DEFS as ROLE_DEFS,
  PARTNER_ROLE_LABELS as ROLE_LABELS,
  PARTNER_ROLE_COMMON_CAPABILITIES as ROLE_COMMON_CAPS,
} from '../constants/partnerRoleDefs.js';
import {
  normalizePartnerScheduleValue,
  PARTNER_SCHEDULE_OPTIONS,
  roleNeedsPartnerSchedule,
} from '../constants/partnerSchedule.js';
import { fetchPartnerTicketSalesEligibility } from '../../lib/partnerTicketSales.js';
import { AD_SLOT_OPTIONS as AD_SLOTS } from '../../lib/adSlotsConfig.js';

/** UUID del perfil del equipo DreamVinyl para hilos partner_support. Opcional: REACT_APP_PARTNER_SUPPORT_PROFILE_ID en .env */
function getSupportProfileId() {
  const v = process.env.REACT_APP_PARTNER_SUPPORT_PROFILE_ID;
  return v && /^[0-9a-f-]{36}$/i.test(String(v).trim()) ? String(v).trim() : null;
}

/** Tras la primera solicitud de ampliación de roles (gratis), cada rol solicitado cuesta estos grooves */
const GROOVES_PER_ROLE_REQUEST = 100;
const MIN_ROLE_REQUEST_REASON_LENGTH = 20;

const PLACEHOLDER_MEDIA =
  'https://placehold.co/1200x400/1a1a24/c8a84b/png?text=DREAMVINYL+%7C+Borrador';

const PLACEHOLDER_BG = 'rgba(200,168,75,0.04)';
const BORDER_GOLD = 'rgba(200,168,75,0.15)';

const MENU_GROUPS = [
  { id: 'pg_inicio', title: 'Inicio', emoji: '🏠', items: [{ id: 'inicio', label: 'Inicio', functional: true }] },
  {
    id: 'pg_cuenta',
    title: 'Cuenta',
    emoji: '👤',
    items: [
      { id: 'perfil', label: 'Mi perfil partner', functional: true },
      { id: 'roles', label: 'Mis roles', functional: true },
    ],
  },
  {
    id: 'pg_actividad',
    title: 'Actividad',
    emoji: '📣',
    items: [
      { id: 'catalogo', label: 'Catálogo', functional: false },
      { id: 'custodia', label: 'Custodia', functional: false },
      { id: 'punto_pickup', label: 'Punto DreamVinyl', functional: false },
      { id: 'proponer_noticia', label: 'Proponer noticia', functional: true },
      { id: 'publicidad', label: 'Publicidad', functional: true },
      { id: 'valoraciones', label: 'Valoraciones', functional: true },
      { id: 'mensajes', label: 'Mensajes', functional: true },
    ],
  },
  {
    id: 'pg_prox',
    title: 'Próximamente',
    emoji: '🚧',
    items: [
      { id: 'eventos', label: 'Eventos', functional: false },
      { id: 'entradas', label: 'Entradas', functional: false },
      { id: 'comunidad', label: 'Comunidad', functional: false },
    ],
  },
  {
    id: 'pg_datos',
    title: 'Datos',
    emoji: '📊',
    items: [
      { id: 'estadisticas', label: 'Estadísticas', functional: true },
      { id: 'grooves', label: 'Grooves', functional: false },
    ],
  },
  { id: 'pg_ajustes', title: 'Ajustes', emoji: '⚙️', items: [{ id: 'ajustes', label: 'Ajustes', functional: false }] },
];

function PlaceholderSoon() {
  return (
    <DCard style={{ padding: 28, textAlign: 'center', background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }}>Próximamente</div>
    </DCard>
  );
}

function parseOtherSocial(raw) {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .slice(0, 3)
    .map(x => ({
      platform: String(x?.platform ?? '').slice(0, 80),
      url: String(x?.url ?? '').trim(),
    }))
    .filter(x => x.platform || x.url);
}

function cardStyle(extra = {}) {
  return {
    padding: 16,
    borderRadius: 12,
    background: 'rgba(255,255,255,0.03)',
    border: `0.5px solid ${BORDER_GOLD}`,
    ...extra,
  };
}

function SectionInicio({ partner, rolesRows, onNavigateSection, menuFlat, canManageListings, hasStoreRole, hasPickupRole }) {
  const [totalAds, setTotalAds] = useState(null);
  const [activeAds, setActiveAds] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const email = partner?.contact_email?.trim();
      if (!email) {
        setTotalAds(0);
        setActiveAds(0);
        return;
      }
      const { data: adv } = await supabase.from('advertisers').select('id').eq('contact_email', email).maybeSingle();
      if (cancelled) return;
      if (!adv?.id) {
        setTotalAds(0);
        setActiveAds(0);
        return;
      }
      const { data: ads } = await supabase.from('ads').select('status, starts_at, ends_at').eq('advertiser_id', adv.id);
      if (cancelled) return;
      const list = ads ?? [];
      setTotalAds(list.length);
      const now = Date.now();
      const act = list.filter(
        a => a.status === 'activo' && new Date(a.starts_at) <= now && new Date(a.ends_at) >= now
      ).length;
      setActiveAds(act);
    })();
    return () => {
      cancelled = true;
    };
  }, [partner?.contact_email]);

  const activeRolesCount = (rolesRows || []).filter(r => r.role_status === 'active' && r.is_active !== false).length;
  const grooves = Number(partner?.grooves_partner ?? 0);

  return (
    <div>
      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginBottom: 18, lineHeight: 1.55 }}>
        Hola, equipo de <strong style={{ color: '#fff' }}>{partner.company_name?.trim() || 'tu negocio'}</strong>. Este es tu espacio
        DreamVinyl Partner (<span style={{ color: C.gold }}>{partner.code}</span>).
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 22,
        }}
      >
        <div style={cardStyle()}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Verificación</div>
          <div style={{ fontSize: 18, fontWeight: 750, color: C.green }}>Verificado</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>Partner aprobado</div>
        </div>
        <div style={cardStyle()}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Roles activos</div>
          <div style={{ fontSize: 18, fontWeight: 750, color: C.gold }}>{activeRolesCount}</div>
        </div>
        <div style={cardStyle()}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Campañas publicidad</div>
          <div style={{ fontSize: 18, fontWeight: 750, color: '#fff' }}>
            {activeAds != null ? activeAds : '—'}
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
              {' '}
              activas{totalAds != null ? ` · ${totalAds} totales` : ''}
            </span>
          </div>
        </div>
        <div style={cardStyle()}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Grooves Partner</div>
          <div style={{ fontSize: 18, fontWeight: 750, color: '#fff' }}>{Number.isFinite(grooves) ? grooves : 0}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 10 }}>Acceso rápido</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {(
          [
            ...(canManageListings ? ['catalogo'] : []),
            ...(hasStoreRole ? ['custodia'] : []),
            ...(hasPickupRole ? ['punto_pickup'] : []),
            'proponer_noticia',
            'perfil',
            'roles',
            'publicidad',
            'valoraciones',
            'mensajes',
            'estadisticas',
          ]
        ).map(id => (
          <BtnGhost key={id} type="button" style={{ fontSize: 12 }} onClick={() => onNavigateSection(id)}>
            {menuFlat.find(m => m.id === id)?.label || id}
          </BtnGhost>
        ))}
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, ...rest }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>{label}</div>
      <input
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 10,
          border: '0.5px solid rgba(255,255,255,0.12)',
          background: 'rgba(0,0,0,0.35)',
          color: '#fff',
          fontSize: 13,
          outline: 'none',
          boxSizing: 'border-box',
        }}
        {...rest}
      />
    </div>
  );
}

function SectionPerfil({ partner, setPartner, onError }) {
  const [companyName, setCompanyName] = useState(partner.company_name ?? '');
  const [contactName, setContactName] = useState(partner.contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(partner.contact_email ?? '');
  const [contactPhone, setContactPhone] = useState(partner.contact_phone ?? '');
  const [address, setAddress] = useState(partner.address ?? '');
  const [city, setCity] = useState(partner.city ?? '');
  const [country, setCountry] = useState(partner.country ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(partner.website_url ?? '');
  const [mapsUrl, setMapsUrl] = useState(partner.maps_url ?? '');
  const [description, setDescription] = useState(partner.description ?? '');
  const [musicGenresLine, setMusicGenresLine] = useState(() =>
    Array.isArray(partner.music_genres) && partner.music_genres.length
      ? partner.music_genres.join(', ')
      : ''
  );
  const [instagramUrl, setInstagramUrl] = useState(partner.instagram_url ?? '');
  const [youtubeUrl, setYoutubeUrl] = useState(partner.youtube_url ?? '');
  const [spotifyUrl, setSpotifyUrl] = useState(partner.spotify_url ?? '');
  const [bandcampUrl, setBandcampUrl] = useState(partner.bandcamp_url ?? '');
  const [tiktokUrl, setTiktokUrl] = useState(partner.tiktok_url ?? '');
  const [facebookUrl, setFacebookUrl] = useState(partner.facebook_url ?? '');
  const [otherLinks, setOtherLinks] = useState(() => parseOtherSocial(partner.other_social_links));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCompanyName(partner.company_name ?? '');
    setContactName(partner.contact_name ?? '');
    setContactEmail(partner.contact_email ?? '');
    setContactPhone(partner.contact_phone ?? '');
    setAddress(partner.address ?? '');
    setCity(partner.city ?? '');
    setCountry(partner.country ?? '');
    setWebsiteUrl(partner.website_url ?? '');
    setMapsUrl(partner.maps_url ?? '');
    setDescription(partner.description ?? '');
    setMusicGenresLine(
      Array.isArray(partner.music_genres) && partner.music_genres.length ? partner.music_genres.join(', ') : ''
    );
    setInstagramUrl(partner.instagram_url ?? '');
    setYoutubeUrl(partner.youtube_url ?? '');
    setSpotifyUrl(partner.spotify_url ?? '');
    setBandcampUrl(partner.bandcamp_url ?? '');
    setTiktokUrl(partner.tiktok_url ?? '');
    setFacebookUrl(partner.facebook_url ?? '');
    setOtherLinks(parseOtherSocial(partner.other_social_links));
  }, [partner]);

  const setOtherAt = (idx, field, val) => {
    setOtherLinks(prev => {
      const next = [...prev];
      while (next.length < 3) next.push({ platform: '', url: '' });
      next[idx] = { ...next[idx], [field]: val };
      return next.slice(0, 3);
    });
  };

  const save = async () => {
    onError('');
    const mapsTrim = mapsUrl.trim();
    if (mapsTrim && !isGoogleMapsLocationUrl(mapsTrim)) {
      onError(
        'El enlace de ubicación no es válido. Solo se admiten URLs de Google Maps (por ejemplo google.com/maps, maps.google.com, maps.app.goo.gl o goo.gl/maps/…).'
      );
      return;
    }
    const cleanedOthers = parseOtherSocial(otherLinks);
    setSaving(true);
    const genres = musicGenresLine
      .split(/[,;\n]+/)
      .map(s => s.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .slice(0, 16);
    const payload = {
      company_name: companyName.trim() || null,
      contact_name: contactName.trim() || null,
      contact_email: contactEmail.trim() || null,
      contact_phone: contactPhone.trim() || null,
      address: address.trim() || null,
      city: city.trim() || null,
      country: country.trim() || null,
      website_url: websiteUrl.trim() || null,
      maps_url: mapsTrim ? normalizeHttpsUrl(mapsTrim) : null,
      description: description.trim() || null,
      music_genres: genres.length ? genres : [],
      instagram_url: instagramUrl.trim() || null,
      youtube_url: youtubeUrl.trim() || null,
      spotify_url: spotifyUrl.trim() || null,
      bandcamp_url: bandcampUrl.trim() || null,
      tiktok_url: tiktokUrl.trim() || null,
      facebook_url: facebookUrl.trim() || null,
      other_social_links: cleanedOthers.length ? cleanedOthers : [],
    };
    const { data, error } = await supabase.from('partners').update(payload).eq('id', partner.id).select().single();
    setSaving(false);
    if (error) {
      onError(error.message || 'No se pudo guardar');
      return;
    }
    setPartner(p => ({ ...p, ...data, partner_roles: p.partner_roles }));
  };

  const extraSlots = Math.max(0, 3 - otherLinks.length);
  const displayOthers =
    otherLinks.length >= 3 ? otherLinks : [...otherLinks, ...Array.from({ length: extraSlots }, () => ({ platform: '', url: '' }))].slice(0, 3);

  return (
    <DCard style={{ padding: 20, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
      <h2 style={{ fontSize: 16, margin: '0 0 16px', color: C.gold }}>Datos del Partner</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0 16px' }}>
        <LabeledInput label="Nombre comercial" value={companyName} onChange={e => setCompanyName(e.target.value)} />
        <LabeledInput label="Persona de contacto" value={contactName} onChange={e => setContactName(e.target.value)} />
        <LabeledInput label="Email" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
        <LabeledInput label="Teléfono" value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
        <LabeledInput label="Dirección" value={address} onChange={e => setAddress(e.target.value)} />
        <LabeledInput label="Ciudad" value={city} onChange={e => setCity(e.target.value)} />
        <LabeledInput label="País" value={country} onChange={e => setCountry(e.target.value)} />
        <LabeledInput label="Web" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} />
        <div style={{ gridColumn: '1 / -1' }}>
          <LabeledInput
            label="Ubicación en Google Maps (opcional)"
            value={mapsUrl}
            onChange={e => setMapsUrl(e.target.value)}
            placeholder="https://maps.app.goo.gl/… o https://www.google.com/maps/…"
          />
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '6px 0 0', lineHeight: 1.45 }}>
            Solo enlaces de Google Maps. Se muestra en la ficha pública «Descubre lugares» cuando lo guardes.
          </p>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Descripción</div>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={4}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 13,
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      </div>
      <div style={{ marginTop: 12 }}>
        <LabeledInput
          label="Géneros musicales (público en «Descubre lugares», separados por coma)"
          value={musicGenresLine}
          onChange={e => setMusicGenresLine(e.target.value)}
          placeholder="Ej: soul, jazz, electrónica"
        />
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '6px 0 0', lineHeight: 1.45 }}>
          Máximo 16 etiquetas. Visible si tienes rol tienda, punto de recogida o sala / venue aprobado.
        </p>
      </div>
      <h3 style={{ fontSize: 14, margin: '20px 0 12px', color: 'rgba(255,255,255,0.75)' }}>Redes sociales</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0 16px' }}>
        <LabeledInput label="Instagram" value={instagramUrl} onChange={e => setInstagramUrl(e.target.value)} />
        <LabeledInput label="YouTube" value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)} />
        <LabeledInput label="Spotify" value={spotifyUrl} onChange={e => setSpotifyUrl(e.target.value)} />
        <LabeledInput label="Bandcamp" value={bandcampUrl} onChange={e => setBandcampUrl(e.target.value)} />
        <LabeledInput label="TikTok" value={tiktokUrl} onChange={e => setTiktokUrl(e.target.value)} />
        <LabeledInput label="Facebook" value={facebookUrl} onChange={e => setFacebookUrl(e.target.value)} />
      </div>
      <h3 style={{ fontSize: 14, margin: '20px 0 12px', color: 'rgba(255,255,255,0.75)' }}>Enlaces adicionales (máx. 3)</h3>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <input
            placeholder="Plataforma"
            value={displayOthers[i]?.platform ?? ''}
            onChange={e => setOtherAt(i, 'platform', e.target.value)}
            style={{
              flex: '1 1 140px',
              padding: '9px 11px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
            }}
          />
          <input
            placeholder="https://…"
            value={displayOthers[i]?.url ?? ''}
            onChange={e => setOtherAt(i, 'url', e.target.value)}
            style={{
              flex: '2 1 220px',
              padding: '9px 11px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
            }}
          />
        </div>
      ))}
      <BtnGold type="button" style={{ marginTop: 14, width: 'auto', minWidth: 160 }} disabled={saving} onClick={save}>
        {saving ? 'Guardando…' : 'Guardar cambios'}
      </BtnGold>
    </DCard>
  );
}

function SectionRoles({ partner, setPartner, rolesRows, refetchRoles, onError, onNotifySuccess, onClearPanelFeedback }) {
  const [modal, setModal] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [roleReasons, setRoleReasons] = useState({});
  /** Horario por rol (store | pickup | venue), igual que en el alta Partner */
  const [scheduleByRole, setScheduleByRole] = useState({});
  const [submitting, setSubmitting] = useState(false);
  /** Error visible dentro del modal (la barra superior queda detrás del overlay) */
  const [modalError, setModalError] = useState('');
  const [deactivatingId, setDeactivatingId] = useState(null);

  const freeWaveUsed = partner?.role_change_free_used === true;
  const grooves = Number(partner?.grooves_partner ?? 0);

  const active = (rolesRows || []).filter(r => r.role_status === 'active' && r.is_active !== false);
  const pending = (rolesRows || []).filter(r => r.role_status === 'pending_approval');

  const requestable = ROLE_DEFS.filter(d => {
    const row = rolesRows?.find(r => r.role === d.key);
    if (!row) return true;
    if (row.role_status === 'rejected') return true;
    if (row.role_status === 'active' && row.is_active === false) return true;
    return false;
  });

  useEffect(() => {
    if (!modal) return;
    setSelectedKeys([]);
    setRoleReasons({});
    setScheduleByRole({});
    setModalError('');
  }, [modal]);

  const closeRoleModal = () => {
    setModal(false);
    setModalError('');
  };

  const toggleKey = key => {
    setSelectedKeys(prev => {
      if (prev.includes(key)) {
        setRoleReasons(rr => {
          const { [key]: _, ...rest } = rr;
          return rest;
        });
        setScheduleByRole(sr => {
          const { [key]: _, ...rest } = sr;
          return rest;
        });
        return prev.filter(k => k !== key);
      }
      const existingRow = rolesRows.find(r => r.role === key);
      const inferred = normalizePartnerScheduleValue(existingRow?.role_data?.schedule_type);
      if (roleNeedsPartnerSchedule(key)) {
        setScheduleByRole(sr => ({ ...sr, [key]: inferred }));
      }
      return [...prev, key];
    });
  };

  const setReason = (key, text) => {
    setRoleReasons(prev => ({ ...prev, [key]: text }));
  };

  const buildRolePayload = (reasonTrimmed, existingData, roleKey) => {
    const merged = existingData && typeof existingData === 'object' ? { ...existingData } : {};
    const payload = {
      ...merged,
      partner_request_reason: reasonTrimmed,
      partner_request_at: new Date().toISOString(),
    };
    if (roleNeedsPartnerSchedule(roleKey)) {
      payload.schedule_type = normalizePartnerScheduleValue(scheduleByRole[roleKey]);
    }
    return payload;
  };

  const submitRequest = async () => {
    if (!selectedKeys.length) return;
    onError('');
    setModalError('');
    for (const key of selectedKeys) {
      const raw = String(roleReasons[key] ?? '').trim();
      if (raw.length < MIN_ROLE_REQUEST_REASON_LENGTH) {
        const m = `Escribe al menos ${MIN_ROLE_REQUEST_REASON_LENGTH} caracteres de motivo para «${ROLE_LABELS[key]}».`;
        setModalError(m);
        return;
      }
      const mod = moderateText(raw, 'listing_description');
      if (!mod.approved) {
        setModalError(mod.reason || 'Motivo no válido. Revísalo e inténtalo de nuevo.');
        return;
      }
    }
    const paidWave = freeWaveUsed;
    const cost = paidWave ? selectedKeys.length * GROOVES_PER_ROLE_REQUEST : 0;
    if (paidWave && grooves < cost) {
      setModalError(
        `Necesitas ${cost} grooves (${GROOVES_PER_ROLE_REQUEST} por cada rol). Tu saldo actual es ${grooves}. Recarga grooves o marca menos roles.`
      );
      return;
    }
    setSubmitting(true);
    try {
      let applied = 0;
      for (const key of selectedKeys) {
        const reasonTrimmed = String(roleReasons[key] ?? '').replace(/\s+/g, ' ').trim();
        const existing = rolesRows.find(r => r.role === key);
        const role_data = buildRolePayload(reasonTrimmed, existing?.role_data, key);
        if (existing?.role_status === 'rejected' || (existing?.role_status === 'active' && existing?.is_active === false)) {
          const { error } = await supabase
            .from('partner_roles')
            .update({ role_status: 'pending_approval', is_active: true, role_data })
            .eq('id', existing.id);
          if (error) throw error;
          applied += 1;
        } else if (!existing) {
          const { error } = await supabase.from('partner_roles').insert({
            partner_id: partner.id,
            role: key,
            role_status: 'pending_approval',
            is_active: true,
            role_data,
          });
          if (error) throw error;
          applied += 1;
        } else if (existing?.role_status === 'pending_approval') {
          throw new Error(`El rol «${ROLE_LABELS[key] || key}» ya está pendiente de revisión.`);
        } else {
          throw new Error(
            `El rol «${ROLE_LABELS[key] || key}» ya está activo. Desactívalo en «Mis roles» antes de volver a solicitarlo.`
          );
        }
      }
      if (applied !== selectedKeys.length) {
        throw new Error('No se registró ningún rol. Revisa el estado de tus roles e inténtalo de nuevo.');
      }
      const nextGrooves = paidWave ? Math.max(0, grooves - cost) : grooves;
      const { error: pErr } = await supabase
        .from('partners')
        .update({
          role_change_free_used: true,
          grooves_partner: nextGrooves,
        })
        .eq('id', partner.id);
      if (pErr) throw pErr;
      setPartner(p => ({
        ...p,
        role_change_free_used: true,
        grooves_partner: nextGrooves,
      }));
      setSelectedKeys([]);
      setRoleReasons({});
      setScheduleByRole({});
      setModalError('');
      await refetchRoles();
      const n = selectedKeys.length;
      const rolWord = n === 1 ? 'rol' : 'roles';
      let okMsg = `Solicitud enviada correctamente (${n} ${rolWord}). El equipo la revisará; el estado aparece abajo en «Pendientes de aprobación».`;
      if (paidWave && cost > 0) {
        okMsg += ` Se han descontado ${cost} grooves de tu saldo.`;
      }
      onNotifySuccess?.(okMsg);
      onError('');
      closeRoleModal();
    } catch (e) {
      const detail = e?.message ? String(e.message) : '';
      const hint =
        'No se ha podido completar el envío. Comprueba la conexión, revisa los datos e inténtalo de nuevo.';
      setModalError(detail ? `${hint} (${detail})` : hint);
      onError(detail || hint);
    } finally {
      setSubmitting(false);
    }
  };

  const deactivateRole = async r => {
    if (deactivatingId) return;
    const label = ROLE_LABELS[r.role] || r.role;
    if (
      !window.confirm(
        `¿Dar de baja el rol «${label}»?\nLo desactivamos en tu panel; puedes solicitarlo de nuevo cuando lo necesites (según grooves si ya usaste la solicitud gratuita).`
      )
    )
      return;
    setDeactivatingId(r.id);
    onError('');
    try {
      const { error } = await supabase.from('partner_roles').update({ is_active: false }).eq('id', r.id).eq('partner_id', partner.id);
      if (error) throw error;
      await refetchRoles();
    } catch (e) {
      onError(e?.message || 'No se pudo dar de baja el rol');
    } finally {
      setDeactivatingId(null);
    }
  };

  const pendingCostPreview = modal && selectedKeys.length && freeWaveUsed ? selectedKeys.length * GROOVES_PER_ROLE_REQUEST : 0;

  const roleRowStatus = r => {
    if (!r) return null;
    if (r.role_status === 'pending_approval') return { label: 'En revisión', tone: 'warn' };
    if (r.role_status === 'active' && r.is_active !== false) return { label: 'Activo en tu cuenta', tone: 'ok' };
    if (r.role_status === 'active' && r.is_active === false) return { label: 'Dado de baja', tone: 'muted' };
    if (r.role_status === 'rejected') return { label: 'Solicitud no aprobada', tone: 'danger' };
    return null;
  };

  return (
    <div>
      <style>{`
        @media (max-width: 560px) {
          .partner-roles-catalog-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <h2 style={{ fontSize: 16, margin: '0 0 14px', color: C.gold }}>Mis roles</h2>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 14,
          alignItems: 'stretch',
          marginBottom: 14,
        }}
      >
        <DCard style={{ flex: '1 1 280px', minWidth: 0, padding: 16, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Roles activos</div>
          {!active.length ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>Ninguno activo todavía.</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', color: 'rgba(255,255,255,0.85)' }}>
              {active.map(r => {
                const def = ROLE_DEFS.find(d => d.key === r.role);
                const iconName = def?.icon || 'store';
                return (
                  <li
                    key={r.id}
                    style={{
                      marginBottom: 10,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 10,
                      paddingBottom: 10,
                      borderBottom: '0.5px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 9,
                          background: 'rgba(200,168,75,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon name={iconName} size={15} color={C.gold} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                          {def?.emoji ? `${def.emoji} ` : ''}
                          {ROLE_LABELS[r.role] || r.role}
                        </div>
                        {def?.desc ? (
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginTop: 4, lineHeight: 1.4 }}>
                            {def.desc}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <BtnGhost
                      type="button"
                      style={{ width: 'auto', padding: '6px 12px', fontSize: 11, flexShrink: 0, alignSelf: 'flex-start' }}
                      disabled={deactivatingId === r.id}
                      onClick={() => deactivateRole(r)}
                    >
                      {deactivatingId === r.id ? '…' : 'Dar de baja'}
                    </BtnGhost>
                  </li>
                );
              })}
            </ul>
          )}
        </DCard>

        <div style={{ flex: '0 1 260px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <BtnGold
            type="button"
            style={{ width: '100%' }}
            onClick={() => {
              onClearPanelFeedback?.();
              setModal(true);
            }}
            disabled={!requestable.length}
          >
            Solicitar nuevo rol
          </BtnGold>
          <div
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 1.55,
              padding: 12,
              borderRadius: 12,
              background: 'rgba(200,168,75,0.06)',
              border: '0.5px solid rgba(200,168,75,0.22)',
              flex: 1,
            }}
          >
            <strong style={{ color: C.gold }}>Coste en grooves</strong>
            <span style={{ color: 'rgba(255,255,255,0.35)' }}>.</span>{' '}
            {freeWaveUsed ? (
              <>
                Tras la solicitud gratuita, cada rol nuevo cuesta <strong>{GROOVES_PER_ROLE_REQUEST}</strong> grooves (saldo:{' '}
                <strong style={{ color: '#fff' }}>{grooves}</strong>). Dar de baja un rol aprobado no consume grooves.
              </>
            ) : (
              <>
                Tu <strong>primera</strong> ampliación de roles es <strong>sin coste</strong>. A partir de ahí,{' '}
                <strong>{GROOVES_PER_ROLE_REQUEST}</strong> grooves por rol nuevo.
              </>
            )}
          </div>
        </div>
      </div>

      <DCard style={{ padding: 16, marginBottom: 14, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Pendientes de aprobación</div>
        {!pending.length ? (
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>No hay solicitudes pendientes.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', color: 'rgba(255,255,255,0.85)' }}>
            {pending.map(r => {
              const pDef = ROLE_DEFS.find(d => d.key === r.role);
              const reason = r.role_data?.partner_request_reason;
              const schRaw = r.role_data?.schedule_type;
              const schNorm = normalizePartnerScheduleValue(schRaw);
              const schOpt = PARTNER_SCHEDULE_OPTIONS.find(o => o.value === schNorm);
              return (
                <li key={r.id} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontWeight: 600 }}>
                    {pDef?.emoji ? `${pDef.emoji} ` : ''}
                    {ROLE_LABELS[r.role] || r.role}
                  </div>
                  {roleNeedsPartnerSchedule(r.role) && schOpt ? (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.48)', marginTop: 5 }}>
                      Horario: <strong style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{schOpt.title}</strong>{' '}
                      <span style={{ color: 'rgba(255,255,255,0.38)' }}>({schOpt.desc})</span>
                    </div>
                  ) : null}
                  {reason ? (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 6, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                      Motivo: {reason}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </DCard>

      <DCard style={{ padding: 16, marginBottom: 4, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Roles en DreamVinyl</div>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: '0 0 12px', lineHeight: 1.45 }}>
          Ocho perfiles combinables; el equipo valida cada solicitud. Lo implementado en la app puede ir ampliándose por fases.
        </p>
        <DCard
          style={{
            padding: '12px 14px',
            marginBottom: 14,
            background: 'rgba(83,74,183,0.06)',
            borderColor: 'rgba(83,74,183,0.22)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.88)', marginBottom: 8 }}>Funciones comunes a todos</div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 10,
              color: 'rgba(255,255,255,0.48)',
              lineHeight: 1.45,
            }}
          >
            {ROLE_COMMON_CAPS.map(line => (
              <li key={line} style={{ marginBottom: 5 }}>
                {line}
              </li>
            ))}
          </ul>
        </DCard>
        <div style={{ fontSize: 10, fontWeight: 650, color: 'rgba(255,255,255,0.4)', marginBottom: 8, letterSpacing: '0.04em' }}>
          FUNCIONES POR ROL
        </div>
        <div
          className="partner-roles-catalog-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 10,
            alignItems: 'stretch',
          }}
        >
          {ROLE_DEFS.map(d => {
            const row = rolesRows?.find(r => r.role === d.key);
            const st = roleRowStatus(row);
            const badgeColor =
              st?.tone === 'ok'
                ? 'rgba(29,158,117,0.2)'
                : st?.tone === 'warn'
                  ? 'rgba(239,159,39,0.18)'
                  : st?.tone === 'danger'
                    ? 'rgba(226,75,74,0.16)'
                    : 'rgba(255,255,255,0.06)';
            const badgeTextColor =
              st?.tone === 'ok' ? C.green : st?.tone === 'warn' ? C.warn : st?.tone === 'danger' ? C.danger : 'rgba(255,255,255,0.4)';
            return (
              <div
                key={d.key}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  padding: '11px 12px',
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.22)',
                  border: '0.5px solid rgba(255,255,255,0.08)',
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: 'rgba(200,168,75,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={d.icon} size={15} color={C.gold} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                      {d.emoji ? `${d.emoji} ` : ''}
                      {d.label}
                    </span>
                    {st ? (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 8,
                          background: badgeColor,
                          color: badgeTextColor,
                        }}
                      >
                        {st.label}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45, marginBottom: 6 }}>{d.desc}</div>
                  {Array.isArray(d.capabilities) && d.capabilities.length ? (
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: 16,
                        fontSize: 9,
                        color: 'rgba(255,255,255,0.42)',
                        lineHeight: 1.38,
                      }}
                    >
                      {d.capabilities.map(line => (
                        <li key={line} style={{ marginBottom: 3 }}>
                          {line}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </DCard>

      {modal ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => !submitting && closeRoleModal()}
        >
          <div
            role="presentation"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <DCard style={{ padding: 20, background: '#12121a', borderColor: BORDER_GOLD }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: '#fff' }}>Solicitar roles</div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '0 0 14px', lineHeight: 1.45 }}>
                Marca cada rol y explica por qué lo necesitas. El equipo revisará cada solicitud.
                {freeWaveUsed ? (
                  <>
                    {' '}
                    Coste previsto si envías esta solicitud: <strong style={{ color: C.gold }}>{pendingCostPreview}</strong> grooves (
                    {GROOVES_PER_ROLE_REQUEST} × roles seleccionados).
                  </>
                ) : (
                  <> Esta vez no se descuentan grooves (primera solicitud gratuita).                  </>
                )}
              </p>
              {modalError ? (
                <div
                  role="alert"
                  style={{
                    marginBottom: 14,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'rgba(226,75,74,0.14)',
                    border: '0.5px solid rgba(226,75,74,0.35)',
                    color: '#f0b4b4',
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  {modalError}
                </div>
              ) : null}
              {!requestable.length ? (
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>No quedan roles disponibles para solicitar.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
                  {requestable.map(d => {
                    const sel = selectedKeys.includes(d.key);
                    return (
                      <div
                        key={d.key}
                        style={{
                          borderRadius: 10,
                          padding: 12,
                          background: 'rgba(0,0,0,0.25)',
                          border: '0.5px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={sel}
                            onChange={() => toggleKey(d.key)}
                            style={{ marginTop: 3, flexShrink: 0 }}
                          />
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 8,
                              background: sel ? 'rgba(200,168,75,0.14)' : 'rgba(255,255,255,0.06)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Icon name={d.icon} size={14} color={sel ? C.gold : 'rgba(255,255,255,0.4)'} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: sel ? '#fff' : 'rgba(255,255,255,0.88)' }}>
                              {d.emoji ? `${d.emoji} ` : ''}
                              {d.label}
                            </div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', marginTop: 5, lineHeight: 1.45 }}>{d.desc}</div>
                          </div>
                        </label>
                        {sel ? (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
                              Motivo (mín. {MIN_ROLE_REQUEST_REASON_LENGTH} caracteres)
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'stretch' }}>
                              <div style={{ flex: '1 1 232px', minWidth: 0 }}>
                                <textarea
                                  value={roleReasons[d.key] ?? ''}
                                  onChange={e => setReason(d.key, e.target.value)}
                                  rows={4}
                                  placeholder="Describe tu actividad y por qué encaja ese rol…"
                                  style={{
                                    width: '100%',
                                    minHeight: 100,
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    border: '0.5px solid rgba(255,255,255,0.12)',
                                    background: 'rgba(0,0,0,0.35)',
                                    color: '#fff',
                                    fontSize: 13,
                                    resize: 'vertical',
                                    boxSizing: 'border-box',
                                  }}
                                />
                              </div>
                              {roleNeedsPartnerSchedule(d.key) ? (
                                <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: 'rgba(255,255,255,0.42)',
                                      marginBottom: 8,
                                      letterSpacing: '0.06em',
                                      textTransform: 'uppercase',
                                      fontWeight: 650,
                                    }}
                                  >
                                    Horario operativo
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                    {PARTNER_SCHEDULE_OPTIONS.map(opt => {
                                      const cur = normalizePartnerScheduleValue(scheduleByRole[d.key]);
                                      const picked = cur === opt.value;
                                      return (
                                        <button
                                          key={opt.value}
                                          type="button"
                                          onClick={() => setScheduleByRole(sr => ({ ...sr, [d.key]: opt.value }))}
                                          style={{
                                            gridColumn: opt.value === 'special' ? '1 / -1' : undefined,
                                            textAlign: 'left',
                                            padding: '8px 10px',
                                            borderRadius: 10,
                                            border: `0.5px solid ${picked ? 'rgba(29,158,117,0.55)' : 'rgba(255,255,255,0.10)'}`,
                                            background: picked ? 'rgba(29,158,117,0.08)' : 'rgba(255,255,255,0.03)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            gap: 8,
                                            alignItems: 'flex-start',
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: 26,
                                              height: 26,
                                              borderRadius: 8,
                                              background: picked ? 'rgba(29,158,117,0.16)' : 'rgba(255,255,255,0.06)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flexShrink: 0,
                                              marginTop: 1,
                                            }}
                                          >
                                            <Icon name={opt.icon} size={12} color={picked ? C.green : 'rgba(255,255,255,0.45)'} />
                                          </div>
                                          <div style={{ minWidth: 0 }}>
                                            <div
                                              style={{
                                                fontSize: 10,
                                                fontWeight: 600,
                                                color: picked ? '#fff' : 'rgba(255,255,255,0.78)',
                                              }}
                                            >
                                              {opt.title}
                                            </div>
                                            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2, lineHeight: 1.3 }}>
                                              {opt.desc}
                                            </div>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.32)', marginTop: 8, lineHeight: 1.35 }}>
                                    Mismas opciones que en el alta Partner (tienda de música, Punto DreamVinyl, cultura y ocio).
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <BtnGhost type="button" onClick={() => !submitting && closeRoleModal()} disabled={submitting}>
                  Cancelar
                </BtnGhost>
                <BtnGold type="button" onClick={submitRequest} disabled={submitting || !selectedKeys.length}>
                  {submitting ? 'Enviando…' : freeWaveUsed && selectedKeys.length ? `Enviar (${selectedKeys.length * GROOVES_PER_ROLE_REQUEST} grooves)` : 'Enviar solicitud'}
                </BtnGold>
              </div>
            </DCard>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionPublicidad({ partner, onError, onSidebarRefresh }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [slot, setSlot] = useState(AD_SLOTS[0].value);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const email = partner.contact_email?.trim();
    if (!email) {
      setRows([]);
      setLoading(false);
      return;
    }
    const { data: adv } = await supabase.from('advertisers').select('id').eq('contact_email', email).maybeSingle();
    if (!adv?.id) {
      setRows([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('ads')
      .select('id, campaign_name, slot, status, starts_at, ends_at, created_at')
      .eq('advertiser_id', adv.id)
      .order('created_at', { ascending: false });
    setRows(data ?? []);
    setLoading(false);
  }, [partner.contact_email]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCampaign = async () => {
    if (!startsAt || !endsAt) {
      onError('Indica fecha de inicio y fin');
      return;
    }
    const s = new Date(startsAt);
    const e = new Date(endsAt);
    if (!(e > s)) {
      onError('La fecha de fin debe ser posterior al inicio');
      return;
    }
    setBusy(true);
    onError('');
    try {
      const email = partner.contact_email?.trim();
      if (!email) throw new Error('Falta email de contacto del partner');
      let advId;
      const { data: existingAdv } = await supabase.from('advertisers').select('id').eq('contact_email', email).maybeSingle();
      if (existingAdv?.id) {
        advId = existingAdv.id;
      } else {
        const { data: insAdv, error: errAdv } = await supabase
          .from('advertisers')
          .insert({
            company_name: partner.company_name?.trim() || 'Partner',
            contact_name: partner.contact_name?.trim() || 'Contacto',
            contact_email: email,
            country: 'ES',
            sector: 'otro',
          })
          .select('id')
          .single();
        if (errAdv || !insAdv?.id) throw new Error(errAdv?.message || 'No se pudo crear el anunciante');
        advId = insAdv.id;
      }
      const name = notes.trim() ? `Solicitud: ${notes.trim().slice(0, 80)}` : `Campaña ${slot}`;
      const { error: errAd } = await supabase.from('ads').insert({
        advertiser_id: advId,
        campaign_name: name.slice(0, 200),
        slot,
        media_url: PLACEHOLDER_MEDIA,
        media_type: 'image',
        target_url: 'https://dreamvinyl.es',
        starts_at: s.toISOString(),
        ends_at: e.toISOString(),
        status: 'borrador',
        notes: notes.trim() || null,
      });
      if (errAd) throw new Error(errAd.message);
      setModal(false);
      setNotes('');
      await load();
      onSidebarRefresh?.();
    } catch (e) {
      onError(e?.message || 'Error al crear la campaña');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 16, margin: 0, color: C.gold }}>Publicidad</h2>
        <BtnGold type="button" style={{ width: 'auto', minWidth: 200 }} onClick={() => setModal(true)}>
          Solicitar nueva campaña
        </BtnGold>
      </div>
      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando campañas…</div>
      ) : !rows.length ? (
        <DCard style={{ padding: 16, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
            Aún no hay campañas vinculadas a tu email de Partner. Puedes crear una solicitud en borrador.
          </div>
        </DCard>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map(a => (
            <DCard key={a.id} style={{ padding: 14, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
              <div style={{ fontWeight: 650, marginBottom: 6 }}>{a.campaign_name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                {a.slot} · <span style={{ color: C.gold }}>{a.status}</span>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
                {new Date(a.starts_at).toLocaleDateString('es-ES')} → {new Date(a.ends_at).toLocaleDateString('es-ES')}
              </div>
            </DCard>
          ))}
        </div>
      )}

      {modal ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => !busy && setModal(false)}
        >
          <div role="presentation" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <DCard style={{ padding: 20, background: '#12121a' }}>
            <div style={{ fontWeight: 700, marginBottom: 12, color: '#fff' }}>Nueva campaña (borrador)</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Ubicación (slot)</div>
              <select
                value={slot}
                onChange={e => setSlot(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: '#0a0a10',
                  color: '#fff',
                  fontSize: 13,
                }}
              >
                {AD_SLOTS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <LabeledInput label="Inicio" type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} />
            <LabeledInput label="Fin" type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} />
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>Notas / mensaje</div>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontSize: 13,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <BtnGhost type="button" onClick={() => setModal(false)} disabled={busy}>
                Cancelar
              </BtnGhost>
              <BtnGold type="button" onClick={submitCampaign} disabled={busy}>
                {busy ? 'Creando…' : 'Crear borrador'}
              </BtnGold>
            </div>
            </DCard>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const SectionEstadisticas = memo(function SectionEstadisticas({ user, partner, onError }) {
  const [loading, setLoading] = useState(true);
  const [followers, setFollowers] = useState(null);
  const [listingViewsSum, setListingViewsSum] = useState(null);
  const [reviewsVisible, setReviewsVisible] = useState(null);
  const [adImpressions, setAdImpressions] = useState(0);
  const [adClicks, setAdClicks] = useState(0);
  const [zones, setZones] = useState([]);
  const [zonesHint, setZonesHint] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      setLoading(true);
      onError('');
      setZonesHint('');
      const d30 = new Date(Date.now() - 30 * 864e5).toISOString();
      const email = String(partner?.contact_email || '').trim();

      try {
        const folPromise = supabase.from('follows').select('follower_id', { count: 'exact', head: true }).eq('following_id', user.id);
        const listPromise = supabase.from('listings').select('views').eq('seller_id', user.id);
        const revPromise = supabase.from('reviews').select('id', { count: 'exact', head: true }).eq('reviewed_id', user.id).eq('is_visible', true);
        const zonePromise = supabase.from('user_site_analytics').select('zone').eq('user_id', user.id).gte('created_at', d30).limit(8000);

        const adsPromise =
          email.length > 0
            ? (async () => {
                const { data: adv, error: e1 } = await supabase.from('advertisers').select('id').eq('contact_email', email).maybeSingle();
                if (e1 || !adv?.id) return { sumI: 0, sumC: 0 };
                const { data: rows, error: e2 } = await supabase.from('ads').select('impressions, clicks').eq('advertiser_id', adv.id);
                if (e2) return { sumI: 0, sumC: 0 };
                const list = rows ?? [];
                const sumI = list.reduce((s, r) => s + (Number(r.impressions) || 0), 0);
                const sumC = list.reduce((s, r) => s + (Number(r.clicks) || 0), 0);
                return { sumI, sumC };
              })()
            : Promise.resolve({ sumI: 0, sumC: 0 });

        const [folRes, listRes, revRes, zoneRes, adsPack] = await Promise.all([folPromise, listPromise, revPromise, zonePromise, adsPromise]);

        if (cancelled) return;

        setFollowers(folRes.error ? null : Number(folRes.count ?? 0));
        if (folRes.error && process.env.NODE_ENV === 'development') console.warn('[SectionEstadisticas] follows', folRes.error.message);

        if (listRes.error) {
          setListingViewsSum(null);
          if (process.env.NODE_ENV === 'development') console.warn('[SectionEstadisticas] listings', listRes.error.message);
        } else {
          const list = listRes.data ?? [];
          setListingViewsSum(list.reduce((s, r) => s + (Number(r.views) || 0), 0));
        }

        setReviewsVisible(revRes.error ? null : Number(revRes.count ?? 0));
        if (revRes.error && process.env.NODE_ENV === 'development') console.warn('[SectionEstadisticas] reviews', revRes.error.message);

        setAdImpressions(Number(adsPack.sumI) || 0);
        setAdClicks(Number(adsPack.sumC) || 0);

        if (zoneRes.error) {
          setZones([]);
          setZonesHint(
            zoneRes.error.message ||
              'No se pudieron leer las zonas. Si falta la política en Supabase, aplica la migración «user_site_analytics_select_own».'
          );
        } else {
          const zmap = {};
          for (const r of zoneRes.data ?? []) {
            const z = String(r.zone || 'unknown').slice(0, 80);
            zmap[z] = (zmap[z] ?? 0) + 1;
          }
          setZones(
            Object.entries(zmap)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 14)
              .map(([zone, count]) => ({ zone, count }))
          );
        }
      } catch (e) {
        if (!cancelled) onError(e?.message || 'No se pudieron cargar las estadísticas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, partner?.contact_email, onError]);

  const fmt = v => (v == null ? '—' : Number(v).toLocaleString('es-ES'));
  const hasEmail = Boolean(String(partner?.contact_email || '').trim());

  return (
    <div>
      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 16, maxWidth: 720, lineHeight: 1.5 }}>
        Resumen con datos ya guardados en DreamVinyl. Las cifras de publicidad suman todas las campañas de tu anunciante (mismo email que el partner).
      </p>

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando estadísticas…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
            <div style={cardStyle()}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Seguidores</div>
              <div style={{ fontSize: 20, fontWeight: 750, color: '#fff' }}>{fmt(followers)}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6, lineHeight: 1.35 }}>
                Usuarios que siguen tu perfil público (tabla <code style={{ color: 'rgba(255,255,255,0.45)' }}>follows</code>).
              </div>
            </div>
            <div style={cardStyle()}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Vistas en tus anuncios</div>
              <div style={{ fontSize: 20, fontWeight: 750, color: C.gold }}>{fmt(listingViewsSum)}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6, lineHeight: 1.35 }}>
                Suma de <code style={{ color: 'rgba(255,255,255,0.45)' }}>views</code> en tus listings (mercado y fichas).
              </div>
            </div>
            <div style={cardStyle()}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Valoraciones públicas</div>
              <div style={{ fontSize: 20, fontWeight: 750, color: '#fff' }}>{fmt(reviewsVisible)}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6, lineHeight: 1.35 }}>
                Reviews visibles recibidas como vendedor (<code style={{ color: 'rgba(255,255,255,0.45)' }}>reviews</code>).
              </div>
            </div>
            <div style={cardStyle()}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Impresiones (ads)</div>
              <div style={{ fontSize: 20, fontWeight: 750, color: '#fff' }}>{fmt(adImpressions)}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6, lineHeight: 1.35 }}>
                {hasEmail ? 'Suma en todas tus campañas.' : 'Añade email de contacto al partner para vincular anunciante.'}
              </div>
            </div>
            <div style={cardStyle()}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Clics (ads)</div>
              <div style={{ fontSize: 20, fontWeight: 750, color: '#fff' }}>{fmt(adClicks)}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6, lineHeight: 1.35 }}>Registrados en tus anuncios contratados.</div>
            </div>
          </div>

          <DCard style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Tu actividad en la app (zonas)</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                Fuente <code style={{ color: C.gold }}>user_site_analytics</code>: tus propias visitas por zona (últimos 30 días). Referencia similar al panel admin de estadísticas.
              </div>
              {zonesHint ? (
                <div style={{ fontSize: 11, color: 'rgba(239,159,39,0.95)', marginTop: 8, lineHeight: 1.4 }}>{zonesHint}</div>
              ) : null}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <th style={{ padding: '10px 14px' }}>Zona</th>
                    <th style={{ padding: '10px 14px' }}>Eventos (30 d)</th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map(row => (
                    <tr key={row.zone} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#fff' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{row.zone}</td>
                      <td style={{ padding: '10px 14px' }}>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!zones.length && !zonesHint ? (
                <div style={{ padding: 14, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>Sin datos de zonas en este periodo.</div>
              ) : null}
            </div>
          </DCard>

          <DCard style={{ padding: 14, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.gold, marginBottom: 6 }}>Visitas al perfil (otros usuarios)</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
              Hoy no hay un contador global de «quién abrió tu perfil» en base de datos. Mientras tanto, <strong style={{ color: 'rgba(255,255,255,0.75)' }}>seguidores</strong> y{' '}
              <strong style={{ color: 'rgba(255,255,255,0.75)' }}>vistas en anuncios</strong> sirven como indicadores de alcance público. Cuando exista métrica dedicada, se mostrará aquí.
            </div>
          </DCard>
        </>
      )}
    </div>
  );
});

const partnerCatalogBtnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

const CATALOG_STATUS_TABS = [
  { id: 'all', label: 'Todos', match: () => true },
  { id: 'active', label: 'En venta', match: s => s === 'active' },
  { id: 'paused', label: 'Pausados', match: s => s === 'paused' },
  { id: 'reserved', label: 'Reservados', match: s => s === 'reserved' },
  { id: 'removed', label: 'Retirados', match: s => s === 'removed' },
];

function listingStatusLabelEs(status) {
  const m = { active: 'Activo', paused: 'Pausado', reserved: 'Reservado', removed: 'Retirado' };
  return m[status] || status || '—';
}

const SectionCatalogo = memo(function SectionCatalogo({ user, onError, onNotifySuccess, onNavigate }) {
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    onError('');
    const { data, error } = await fetchSellerListingsForManage(user.id);
    if (error) {
      onError(typeof error === 'string' ? error : 'No se pudieron cargar los anuncios');
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, [user?.id, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const badgeKeys = useMemo(() => {
    const first = rows.find(r => Array.isArray(r.sellerPartnerRoleKeys) && r.sellerPartnerRoleKeys.length);
    return first?.sellerPartnerRoleKeys ?? [];
  }, [rows]);

  const filtered = useMemo(() => {
    const meta = CATALOG_STATUS_TABS.find(t => t.id === tab);
    if (!meta) return rows;
    return rows.filter(r => meta.match(r.status));
  }, [rows, tab]);

  const tabCount = useCallback(
    tabId => {
      const meta = CATALOG_STATUS_TABS.find(t => t.id === tabId);
      if (!meta) return 0;
      if (tabId === 'all') return rows.length;
      return rows.filter(r => meta.match(r.status)).length;
    },
    [rows]
  );

  const applyStatus = async (listingId, status) => {
    if (!user?.id || !listingId) return;
    if (
      status === 'removed' &&
      !window.confirm('¿Retirar este anuncio del marketplace? Quedará oculto; puedes reactivarlo aquí cuando quieras.')
    ) {
      return;
    }
    setBusyId(listingId);
    onError('');
    const { error } = await updateSellerListingStatus(user.id, listingId, status);
    if (error) onError(error);
    else {
      onNotifySuccess?.(
        status === 'active' ? 'Anuncio activado.' : status === 'paused' ? 'Anuncio pausado.' : 'Anuncio retirado.'
      );
      await load();
    }
    setBusyId(null);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
          Roles partner en el marketplace (mismos badges que en tus anuncios públicos)
        </div>
        <PartnerRoleBadges roleKeys={badgeKeys} theme="dark" />
        {!badgeKeys.length ? (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
            Aún no hay roles visibles en perfil; se actualizan al tener roles de venta aprobados y activos.
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => onNavigate && onNavigate('publicar')}>
          + Publicar disco
        </BtnGhost>
        <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => onNavigate && onNavigate('misventas')}>
          Ir a Mis ventas
        </BtnGhost>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {CATALOG_STATUS_TABS.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: `0.5px solid ${tab === t.id ? C.gold : 'rgba(255,255,255,0.12)'}`,
              background: tab === t.id ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
              color: tab === t.id ? C.gold : 'rgba(255,255,255,0.65)',
              fontSize: 12,
              fontWeight: tab === t.id ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            {t.label} ({tabCount(t.id)})
          </button>
        ))}
        <button type="button" onClick={() => load()} style={{ ...partnerCatalogBtnMini, marginLeft: 'auto' }} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </div>

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando catálogo…</div>
      ) : (
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 920 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '10px 12px', width: 56 }}>Foto</th>
                  <th style={{ padding: '10px 12px' }}>Título</th>
                  <th style={{ padding: '10px 12px' }}>Artista</th>
                  <th style={{ padding: '10px 12px' }}>Formato</th>
                  <th style={{ padding: '10px 12px' }}>Precio</th>
                  <th style={{ padding: '10px 12px' }}>Vistas</th>
                  <th style={{ padding: '10px 12px' }}>Estado</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(l => {
                  const thumb = listingThumbUrlFromRow(l);
                  const reserved = l.status === 'reserved';
                  const isBusy = busyId === l.id;
                  return (
                    <tr key={l.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff' }}>
                      <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                        {thumb ? (
                          <img
                            src={thumb}
                            alt=""
                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '0.5px solid rgba(255,255,255,0.12)' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              background: 'rgba(255,255,255,0.06)',
                              fontSize: 9,
                              color: 'rgba(255,255,255,0.35)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            —
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, maxWidth: 200 }}>{l.title}</td>
                      <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.75)' }}>{l.artist}</td>
                      <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>{l.type || '—'}</td>
                      <td style={{ padding: '10px 12px', color: C.gold, whiteSpace: 'nowrap' }}>{Number(l.price).toFixed(2)} €</td>
                      <td style={{ padding: '10px 12px' }}>{l.views ?? 0}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{listingStatusLabelEs(l.status)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {reserved ? (
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', maxWidth: 200, lineHeight: 1.35 }}>
                            Hay una reserva o venta en curso. Gestiona el envío en{' '}
                            <button
                              type="button"
                              onClick={() => onNavigate && onNavigate('misventas')}
                              style={{ background: 'none', border: 'none', color: C.gold, cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 10 }}
                            >
                              Mis ventas
                            </button>
                            .
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {l.status !== 'active' ? (
                              <button
                                type="button"
                                style={{ ...partnerCatalogBtnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                                disabled={isBusy}
                                onClick={() => applyStatus(l.id, 'active')}
                              >
                                Activar
                              </button>
                            ) : null}
                            {l.status === 'active' ? (
                              <button
                                type="button"
                                style={{ ...partnerCatalogBtnMini, borderColor: 'rgba(239,159,39,0.55)', color: 'rgba(239,159,39,0.95)' }}
                                disabled={isBusy}
                                onClick={() => applyStatus(l.id, 'paused')}
                              >
                                Pausar
                              </button>
                            ) : null}
                            {l.status !== 'removed' ? (
                              <button
                                type="button"
                                style={{ ...partnerCatalogBtnMini, borderColor: 'rgba(226,75,74,0.55)', color: C.danger }}
                                disabled={isBusy}
                                onClick={() => applyStatus(l.id, 'removed')}
                              >
                                Retirar
                              </button>
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!filtered.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay anuncios en este filtro.</div>
            ) : null}
          </div>
        </DCard>
      )}
    </div>
  );
});

function initialsForUsername(username) {
  const s = String(username || '').trim();
  if (!s) return '?';
  const parts = s.replace(/^@/, '').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

const SectionValoracionesPartner = memo(function SectionValoracionesPartner({ user, onError, onNotifySuccess, onGoCatalog }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    onError('');
    const { data, error } = await fetchReceivedReviews(user.id, { onlyVisible: false, limit: 120 });
    if (error) {
      onError(typeof error === 'string' ? error : 'No se pudieron cargar las valoraciones');
      setRows([]);
    } else {
      setRows(data);
      const m = {};
      for (const r of data) m[r.id] = r.seller_public_reply ?? '';
      setReplyDrafts(m);
    }
    setLoading(false);
  }, [user?.id, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const setDraft = (id, text) => {
    setReplyDrafts(prev => ({ ...prev, [id]: text }));
  };

  const saveReply = async (reviewId, { clear } = {}) => {
    if (!user?.id || !reviewId || savingId) return;
    const raw = clear ? '' : String(replyDrafts[reviewId] ?? '').trim();
    if (!clear && !raw.length) {
      onError('Escribe una respuesta o usa «Quitar respuesta».');
      return;
    }
    if (!clear && raw.length > 0) {
      const mod = moderateText(raw, 'general');
      if (!mod.approved) {
        onError(mod.reason || 'Texto no permitido');
        return;
      }
    }
    setSavingId(reviewId);
    onError('');
    const { error } = await updateSellerPublicReply(user.id, reviewId, clear ? '' : raw);
    if (error) onError(error);
    else {
      onNotifySuccess?.(clear ? 'Respuesta eliminada.' : 'Respuesta pública guardada.');
      await load();
    }
    setSavingId(null);
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 12, maxWidth: 720, lineHeight: 1.5 }}>
        Valoraciones recibidas (sistema de surcos). Puedes responder en público; la respuesta se muestra en tu perfil de vendedor cuando la valoración es visible.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {typeof onGoCatalog === 'function' ? (
          <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => onGoCatalog()}>
            Ir a Catálogo de anuncios
          </BtnGhost>
        ) : null}
        <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => load()} disabled={loading}>
          {loading ? 'Cargando…' : 'Recargar'}
        </BtnGhost>
      </div>

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Cargando valoraciones…</div>
      ) : !rows.length ? (
        <DCard style={{ padding: 18, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Aún no tienes valoraciones recibidas registradas.</div>
        </DCard>
      ) : (
        rows.map(r => {
          const rev = Array.isArray(r.reviewer) ? r.reviewer[0] : r.reviewer;
          const uname = rev?.username ?? 'Usuario';
          const rating = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 1)));
          const tags = Array.isArray(r.tags) ? r.tags : [];
          const pending = !r.is_visible;
          return (
            <DCard key={r.id} style={{ padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <Avatar initials={initialsForUsername(uname)} bg="#534AB7" color="#EEEDFE" size={36} imageUrl={rev?.avatar_url ?? null} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>@{uname}</div>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                    </span>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <SurcosRating value={rating} readOnly size="sm" showLabel />
                  </div>
                  {pending ? (
                    <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(239,159,39,0.95)', fontWeight: 600 }}>Moderación: aún no visible en público</div>
                  ) : null}
                </div>
              </div>
              {tags.length ? (
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                  {tags.map(t => (
                    <span
                      key={String(t)}
                      style={{
                        fontSize: 9,
                        padding: '2px 8px',
                        borderRadius: 8,
                        background: 'rgba(200,168,75,0.12)',
                        color: C.gold,
                        border: '0.5px solid rgba(200,168,75,0.25)',
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
              {r.comment?.trim() ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', lineHeight: 1.45, marginBottom: 10 }}>{r.comment.trim()}</div>
              ) : (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>Sin comentario escrito.</div>
              )}

              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 6, fontWeight: 600 }}>Tu respuesta pública</div>
              <textarea
                value={replyDrafts[r.id] ?? ''}
                onChange={e => setDraft(r.id, e.target.value)}
                rows={3}
                maxLength={900}
                placeholder="Agradece o aclara algo para quien visite tu perfil…"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontSize: 12,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  marginBottom: 8,
                }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <BtnGold type="button" disabled={savingId === r.id} onClick={() => saveReply(r.id, { clear: false })} style={{ fontSize: 12, padding: '8px 14px' }}>
                  {savingId === r.id ? 'Guardando…' : 'Publicar / actualizar'}
                </BtnGold>
                <BtnGhost type="button" disabled={savingId === r.id || !(r.seller_public_reply || '').trim()} onClick={() => saveReply(r.id, { clear: true })} style={{ fontSize: 12 }}>
                  Quitar respuesta
                </BtnGhost>
              </div>
            </DCard>
          );
        })
      )}
    </div>
  );
});

const SectionMensajes = memo(function SectionMensajes({ user, onError, isSectionActive }) {
  const supportId = getSupportProfileId();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const listEndRef = React.useRef(null);

  const ensureConversation = useCallback(async () => {
    if (!user?.id || !supportId) return null;
    const { data: exist } = await supabase
      .from('conversations')
      .select('id')
      .eq('buyer_id', user.id)
      .eq('seller_id', supportId)
      .is('listing_id', null)
      .eq('conversation_type', 'partner_support')
      .maybeSingle();
    if (exist?.id) return exist.id;
    const { data: ins, error } = await supabase
      .from('conversations')
      .insert({
        buyer_id: user.id,
        seller_id: supportId,
        listing_id: null,
        conversation_type: 'partner_support',
      })
      .select('id')
      .single();
    if (error) {
      console.error(error);
      return null;
    }
    return ins?.id ?? null;
  }, [user?.id, supportId]);

  const loadMessages = useCallback(
    async cid => {
      const { data, error } = await supabase
        .from('messages')
        .select('id, sender_id, content, created_at, moderation_status')
        .eq('conversation_id', cid)
        .order('created_at', { ascending: true });
      if (error) return;
      setMessages(data ?? []);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      if (!supportId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const cid = await ensureConversation();
      if (cancelled) return;
      setConvId(cid);
      if (cid) await loadMessages(cid);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, supportId, ensureConversation, loadMessages]);

  /** Solo suscripción realtime cuando la sección Mensajes está visible; cleanup al cambiar de sección o desmontar. */
  useEffect(() => {
    if (!isSectionActive || !convId || !user?.id) return undefined;

    const channel = supabase
      .channel(`partner-msg:${convId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${convId}` },
        payload => {
          const row = payload.new;
          if (!row?.id) return;
          setMessages(prev => {
            if (prev.some(m => m.id === row.id)) return prev;
            return [
              ...prev,
              {
                id: row.id,
                sender_id: row.sender_id,
                content: row.content,
                created_at: row.created_at,
                moderation_status: row.moderation_status,
              },
            ];
          });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [isSectionActive, convId, user?.id]);

  useEffect(() => {
    if (!isSectionActive) return;
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [isSectionActive, messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || !convId || sending) return;
    const mm = moderateText(t, 'chat');
    if (!mm.approved) {
      onError(mm.reason || 'No se pudo enviar el mensaje');
      return;
    }
    setSending(true);
    onError('');
    const { error } = await supabase.from('messages').insert({
      conversation_id: convId,
      sender_id: user.id,
      content: t,
      is_system: false,
    });
    setSending(false);
    if (error) {
      onError(error.message || 'No se pudo enviar');
      return;
    }
    setText('');
  };

  if (!supportId) {
    return (
      <DCard style={{ padding: 18, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, margin: 0 }}>
          Para chatear con el equipo DreamVinyl, configura en el proyecto la variable de entorno{' '}
          <code style={{ color: C.gold }}>REACT_APP_PARTNER_SUPPORT_PROFILE_ID</code> con el UUID del perfil de soporte (usuario interno).
          Mientras tanto puedes escribirnos en el correo de contacto oficial.
        </p>
      </DCard>
    );
  }

  if (loading) {
    return <div style={{ color: 'rgba(255,255,255,0.45)' }}>Abriendo conversación…</div>;
  }

  return (
    <DCard style={{ padding: 0, overflow: 'hidden', background: '#0e0e14', borderColor: BORDER_GOLD }}>
      <div style={{ padding: '12px 14px', borderBottom: `0.5px solid ${BORDER_GOLD}`, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
        Mensajes con DreamVinyl
      </div>
      <div style={{ height: 320, overflowY: 'auto', padding: 12 }}>
        {!messages.length ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: 20 }}>
            Escribe el primer mensaje para el equipo.
          </div>
        ) : (
          messages.map(m => {
            const mine = m.sender_id === user.id;
            const hidden = String(m.moderation_status || 'approved') === 'hidden';
            return (
              <div
                key={m.id}
                style={{
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: mine ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '8px 11px',
                    borderRadius: 12,
                    fontSize: 13,
                    lineHeight: 1.45,
                    background: mine ? 'rgba(200,168,75,0.22)' : 'rgba(255,255,255,0.06)',
                    color: '#eee',
                    fontStyle: hidden ? 'italic' : undefined,
                    opacity: hidden ? 0.8 : 1,
                  }}
                >
                  {hidden ? 'Mensaje ocultado por moderación' : m.content}
                </div>
              </div>
            );
          })
        )}
        <div ref={listEndRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `0.5px solid ${BORDER_GOLD}` }}>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Escribe un mensaje…"
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.12)',
            background: 'rgba(0,0,0,0.45)',
            color: '#fff',
            fontSize: 13,
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <BtnGold type="button" style={{ width: 'auto', minWidth: 90 }} disabled={sending || !text.trim()} onClick={send}>
          Enviar
        </BtnGold>
      </div>
    </DCard>
  );
}, (prev, next) => prev.user?.id === next.user?.id && prev.isSectionActive === next.isSectionActive && prev.onError === next.onError);

export default function PagePartnerPanel({ onBack, onNavigate, user }) {
  const [bootLoading, setBootLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [rolesRows, setRolesRows] = useState([]);
  const [activeSection, setActiveSection] = useState('inicio');
  const [bannerError, setBannerError] = useState('');
  const [bannerSuccess, setBannerSuccess] = useState('');
  const [asyncSidebarBadges, setAsyncSidebarBadges] = useState({ mensajes: 0, publicidad: 0 });
  const [menuOpenGroupId, setMenuOpenGroupId] = useState('pg_inicio');
  const [ticketSalesEligible, setTicketSalesEligible] = useState(false);

  const onNavigateRef = useRef(onNavigate);
  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        onNavigateRef.current('registro-tienda');
        setBootLoading(false);
        return;
      }
      setBootLoading(true);
      const { data: row, error } = await supabase
        .from('partners')
        .select(
          'id, code, company_name, contact_name, contact_email, contact_phone, address, city, country, website_url, maps_url, description, music_genres, verification_status, grooves_partner, role_change_free_used, instagram_url, youtube_url, spotify_url, bandcamp_url, tiktok_url, facebook_url, other_social_links, partner_roles ( id, role, role_status, is_active, role_data )'
        )
        .eq('profile_id', user.id)
        .eq('verification_status', 'approved')
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error('[PartnerPanel]', error);
        onNavigateRef.current('registro-tienda');
        setBootLoading(false);
        return;
      }
      if (!row?.id) {
        onNavigateRef.current('registro-tienda');
        setBootLoading(false);
        return;
      }
      setPartner(row);
      setRolesRows(row.partner_roles ?? []);
      setBootLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id || !partner?.id) {
        if (!cancelled) setTicketSalesEligible(false);
        return;
      }
      const el = await fetchPartnerTicketSalesEligibility(user.id);
      if (!cancelled) setTicketSalesEligible(!!el.eligible);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, partner?.id]);

  const rolesHeaderLabel = useMemo(() => {
    const activeList = (rolesRows || []).filter(r => r.role_status === 'active' && r.is_active !== false);
    const keys = activeList.map(r => ROLE_LABELS[r.role] || r.role);
    return keys.length ? [...new Set(keys)].join(' · ') : '—';
  }, [rolesRows]);

  const rolesPendingCount = useMemo(
    () => (rolesRows || []).filter(r => r.role_status === 'pending_approval').length,
    [rolesRows]
  );

  const loadPartnerSidebarBadges = useCallback(async () => {
    if (!user?.id || !partner) return;
    const supportId = getSupportProfileId();
    let mensajes = 0;
    if (supportId) {
      const { data: conv } = await supabase
        .from('conversations')
        .select('id')
        .eq('buyer_id', user.id)
        .eq('seller_id', supportId)
        .is('listing_id', null)
        .eq('conversation_type', 'partner_support')
        .maybeSingle();
      if (conv?.id) {
        const { data: readRow } = await supabase
          .from('conversation_reads')
          .select('last_read_at')
          .eq('reader_id', user.id)
          .eq('conversation_id', conv.id)
          .maybeSingle();
        const lr = readRow?.last_read_at;
        let q = supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('conversation_id', conv.id)
          .eq('sender_id', supportId);
        if (lr) q = q.gt('created_at', lr);
        const { count, error } = await q;
        if (!error) mensajes = count ?? 0;
      }
    }
    let publicidad = 0;
    const email = partner.contact_email?.trim();
    if (email) {
      const { data: adv } = await supabase.from('advertisers').select('id').eq('contact_email', email).maybeSingle();
      if (adv?.id) {
        const { count, error } = await supabase
          .from('ads')
          .select('*', { count: 'exact', head: true })
          .eq('advertiser_id', adv.id)
          .in('status', ['borrador', 'pendiente_revision']);
        if (!error) publicidad = count ?? 0;
      }
    }
    setAsyncSidebarBadges({ mensajes, publicidad });
  }, [user?.id, partner]);

  const canManageListings = useMemo(() => partnerHasMarketplaceSellerRoles(rolesRows), [rolesRows]);

  const hasStoreRole = useMemo(() => partnerHasActiveStoreRole(rolesRows), [rolesRows]);

  const hasPickupRole = useMemo(() => partnerHasActivePickupRole(rolesRows), [rolesRows]);

  const pickupRoleRow = useMemo(
    () => (rolesRows || []).find(r => r.role === 'pickup' && r.role_status === 'active' && r.is_active !== false) ?? null,
    [rolesRows]
  );

  const storeRoleRow = useMemo(
    () => (rolesRows || []).find(r => r.role === 'store' && r.role_status === 'active' && r.is_active !== false) ?? null,
    [rolesRows]
  );

  const menuGroups = useMemo(
    () =>
      MENU_GROUPS.map(g => ({
        ...g,
        items: g.items.map(it => {
          if (it.id === 'catalogo') return { ...it, functional: canManageListings };
          if (it.id === 'custodia') return { ...it, functional: hasStoreRole };
          if (it.id === 'punto_pickup') return { ...it, functional: hasPickupRole };
          if (it.id === 'entradas') return { ...it, functional: ticketSalesEligible };
          return it;
        }),
      })),
    [canManageListings, hasStoreRole, hasPickupRole, ticketSalesEligible]
  );

  const menuFlat = useMemo(() => menuGroups.flatMap(g => g.items), [menuGroups]);

  useEffect(() => {
    const g = menuGroups.find(gr => gr.items.some(i => i.id === activeSection));
    if (g) setMenuOpenGroupId(g.id);
  }, [activeSection, menuGroups]);

  useEffect(() => {
    if (partner?.id) loadPartnerSidebarBadges();
  }, [partner?.id, partner?.contact_email, loadPartnerSidebarBadges]);

  useEffect(() => {
    loadPartnerSidebarBadges();
  }, [activeSection, loadPartnerSidebarBadges]);

  const sidebarBadgeFor = useCallback(
    itemId => {
      if (itemId === 'roles') return rolesPendingCount;
      return Number(asyncSidebarBadges[itemId]) || 0;
    },
    [rolesPendingCount, asyncSidebarBadges]
  );

  const refetchRoles = useCallback(async () => {
    if (!partner?.id) return;
    const { data } = await supabase
      .from('partner_roles')
      .select('id, role, role_status, is_active, role_data')
      .eq('partner_id', partner.id);
    setRolesRows(data ?? []);
    loadPartnerSidebarBadges();
  }, [partner?.id, loadPartnerSidebarBadges]);

  if (bootLoading) {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Panel Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.45)' }}>Cargando tu panel…</div>
      </div>
    );
  }

  if (!partner) {
    return null;
  }

  const item = menuFlat.find(m => m.id === activeSection);
  const showFunctional =
    item?.functional &&
    [
      'inicio',
      'perfil',
      'roles',
      'publicidad',
      'mensajes',
      'catalogo',
      'custodia',
      'punto_pickup',
      'proponer_noticia',
      'estadisticas',
      'valoraciones',
      'entradas',
    ].includes(activeSection);
  const showPlaceholder = item && !item.functional;

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <WebTopBar onBack={onBack} title="Panel Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <aside
          style={{
            width: 230,
            flexShrink: 0,
            borderRight: '0.5px solid rgba(200,168,75,0.18)',
            background: 'linear-gradient(180deg, #0c0c12 0%, #0a0a0f 100%)',
            padding: '16px 10px',
            overflowY: 'auto',
          }}
        >
          <div style={{ fontSize: 10, color: 'rgba(200,168,75,0.55)', letterSpacing: '0.12em', margin: '0 8px 12px', fontWeight: 700 }}>
            PANEL PARTNER
          </div>
          {menuGroups.map(group => {
            const open = menuOpenGroupId === group.id;
            const groupBadgeSum = group.items.reduce((acc, it) => acc + sidebarBadgeFor(it.id), 0);
            const groupHasBadge = groupBadgeSum > 0;
            return (
              <div key={group.id} style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={
                    groupHasBadge
                      ? `${group.title} (${groupBadgeSum} pendiente${groupBadgeSum === 1 ? '' : 's'} en el bloque)`
                      : group.title
                  }
                  onClick={() => setMenuOpenGroupId(prev => (prev === group.id ? null : group.id))}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    marginBottom: open ? 4 : 2,
                    borderRadius: 10,
                    border: '0.5px solid rgba(255,255,255,0.08)',
                    background: open ? 'rgba(200,168,75,0.07)' : 'rgba(255,255,255,0.03)',
                    color: open ? C.gold : 'rgba(255,255,255,0.65)',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  title={open ? 'Plegar bloque' : 'Desplegar bloque'}
                >
                  <span style={{ width: 12, flexShrink: 0, fontSize: 9, opacity: 0.75 }} aria-hidden>
                    {open ? '▾' : '▸'}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <span style={{ marginRight: 6 }}>{group.emoji}</span>
                    {group.title}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {groupHasBadge ? (
                      <span
                        title={
                          !open
                            ? `Pendientes en este bloque: ${groupBadgeSum}`
                            : `Hay pendientes en este bloque (${groupBadgeSum})`
                        }
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: 'rgba(226,75,74,0.95)',
                          boxShadow: '0 0 0 2px rgba(10,10,15,0.92)',
                          flexShrink: 0,
                        }}
                        aria-hidden
                      />
                    ) : null}
                    {!open && groupHasBadge ? (
                      <span
                        style={{
                          minWidth: 18,
                          padding: '2px 6px',
                          borderRadius: 999,
                          background: 'rgba(226,75,74,0.95)',
                          color: '#0a0a0f',
                          fontSize: 10,
                          fontWeight: 900,
                          lineHeight: 1.2,
                          textAlign: 'center',
                        }}
                        title="Pendientes en este bloque"
                      >
                        {groupBadgeSum}
                      </span>
                    ) : null}
                  </span>
                </button>
                {open ? (
                  <div style={{ paddingLeft: 6 }}>
                    {group.items.map(menuItem => {
                      const n = sidebarBadgeFor(menuItem.id);
                      return (
                        <button
                          key={menuItem.id}
                          type="button"
                          onClick={() => {
                            setActiveSection(menuItem.id);
                            setBannerError('');
                            setBannerSuccess('');
                            setMenuOpenGroupId(group.id);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '9px 12px',
                            marginBottom: 3,
                            borderRadius: 10,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: activeSection === menuItem.id ? 700 : 500,
                            color: activeSection === menuItem.id ? C.gold : 'rgba(255,255,255,0.55)',
                            background: activeSection === menuItem.id ? 'rgba(200,168,75,0.12)' : 'transparent',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <span>{menuItem.label}</span>
                            {n > 0 ? (
                              <span
                                style={{
                                  minWidth: 18,
                                  padding: '2px 6px',
                                  borderRadius: 999,
                                  background: 'rgba(226,75,74,0.95)',
                                  color: '#0a0a0f',
                                  fontSize: 10,
                                  fontWeight: 900,
                                  lineHeight: 1.2,
                                  textAlign: 'center',
                                }}
                                title="Pendiente"
                              >
                                {n}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </aside>
        <main style={{ flex: 1, padding: '20px 24px 40px', overflowY: 'auto' }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: C.gold, letterSpacing: '0.1em', fontWeight: 800 }}>{partner.code}</div>
            <h1 style={{ fontSize: 20, fontWeight: 750, margin: '6px 0 4px', color: '#fff', letterSpacing: '0.02em' }}>
              {partner.company_name?.trim() || 'Partner DreamVinyl'}
            </h1>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', maxWidth: 720, lineHeight: 1.45 }}>
              Roles activos: <span style={{ color: 'rgba(255,255,255,0.78)' }}>{rolesHeaderLabel}</span>
            </div>
          </div>
          <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(200,168,75,0.35), transparent)', marginBottom: 18, maxWidth: 220 }} />
          {bannerSuccess ? (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                borderRadius: 10,
                background: 'rgba(29,158,117,0.12)',
                border: '0.5px solid rgba(29,158,117,0.28)',
                color: C.green,
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              {bannerSuccess}
            </div>
          ) : null}
          {bannerError ? (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                borderRadius: 10,
                background: 'rgba(226,75,74,0.12)',
                border: '0.5px solid rgba(226,75,74,0.25)',
                color: C.danger,
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              {bannerError}
            </div>
          ) : null}

          {showFunctional && activeSection === 'inicio' ? (
            <SectionInicio
              partner={partner}
              rolesRows={rolesRows}
              onNavigateSection={setActiveSection}
              menuFlat={menuFlat}
              canManageListings={canManageListings}
              hasStoreRole={hasStoreRole}
              hasPickupRole={hasPickupRole}
            />
          ) : null}
          {showFunctional && activeSection === 'perfil' ? (
            <SectionPerfil partner={partner} setPartner={setPartner} onError={setBannerError} />
          ) : null}
          {showFunctional && activeSection === 'roles' ? (
            <SectionRoles
              partner={partner}
              setPartner={setPartner}
              rolesRows={rolesRows}
              refetchRoles={refetchRoles}
              onError={msg => {
                setBannerError(msg);
                if (msg) setBannerSuccess('');
              }}
              onNotifySuccess={msg => {
                setBannerSuccess(msg);
                setBannerError('');
              }}
              onClearPanelFeedback={() => {
                setBannerError('');
                setBannerSuccess('');
              }}
            />
          ) : null}
          {showFunctional && activeSection === 'publicidad' ? (
            <SectionPublicidad partner={partner} onError={setBannerError} onSidebarRefresh={loadPartnerSidebarBadges} />
          ) : null}
          {showFunctional && activeSection === 'custodia' ? (
            <SectionCustodiaPartner
              partner={partner}
              storeRoleRow={storeRoleRow}
              onError={setBannerError}
              onNotifySuccess={msg => {
                setBannerSuccess(msg);
                setBannerError('');
              }}
            />
          ) : null}
          {showFunctional && activeSection === 'punto_pickup' ? (
            <SectionPickupPartner
              partner={partner}
              pickupRoleRow={pickupRoleRow}
              onError={setBannerError}
              onNotifySuccess={msg => {
                setBannerSuccess(msg);
                setBannerError('');
              }}
              refetchRoles={refetchRoles}
            />
          ) : null}
          {showFunctional && activeSection === 'proponer_noticia' ? (
            <SectionNoticiasPartner
              partner={partner}
              onError={setBannerError}
              onNotifySuccess={msg => {
                setBannerSuccess(msg);
                setBannerError('');
              }}
            />
          ) : null}
          {showFunctional && activeSection === 'catalogo' ? (
            <SectionCatalogo
              user={user}
              onError={setBannerError}
              onNotifySuccess={msg => {
                setBannerSuccess(msg);
                setBannerError('');
              }}
              onNavigate={onNavigate}
            />
          ) : null}
          {showFunctional && activeSection === 'estadisticas' ? (
            <SectionEstadisticas user={user} partner={partner} onError={setBannerError} />
          ) : null}
          {showFunctional && activeSection === 'valoraciones' ? (
            <SectionValoracionesPartner
              user={user}
              onError={setBannerError}
              onNotifySuccess={msg => {
                setBannerSuccess(msg);
                setBannerError('');
              }}
              onGoCatalog={canManageListings ? () => setActiveSection('catalogo') : null}
            />
          ) : null}
          {showFunctional && activeSection === 'entradas' ? (
            <DCard style={{ padding: 22, background: PLACEHOLDER_BG, borderColor: BORDER_GOLD }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.gold, marginBottom: 10 }}>Venta de entradas</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, margin: '0 0 14px' }}>
                Tu partner puede publicar eventos en <strong style={{ color: '#fff' }}>Comunidad</strong> con enlace de venta verificado.
                El check-in y la tienda de entradas integrada llegarán en una fase posterior.
              </p>
              <BtnGold
                type="button"
                style={{ width: 'auto', minWidth: 200 }}
                onClick={() => onNavigate?.('comunidad')}
              >
                Ir a Comunidad
              </BtnGold>
            </DCard>
          ) : null}
          {showFunctional && activeSection === 'mensajes' ? (
            <SectionMensajes user={user} onError={setBannerError} isSectionActive />
          ) : null}
          {showPlaceholder ? <PlaceholderSoon /> : null}
        </main>
      </div>
    </div>
  );
}
